/**
 * Texto de template para a prévia do WhatsApp.
 *
 * Interpreta a formatação do WhatsApp (*negrito*, _itálico_, ~tachado~) e as
 * variáveis ({{1}}, {{nome}}). Devolve elementos React, nunca HTML, então o
 * texto do template não precisa ser escapado nem passa por
 * dangerouslySetInnerHTML.
 */

import React from "react";

const VAR_SOURCE = "\\{\\{\\s*([\\w.]+)\\s*\\}\\}";
const FORMAT_REGEX = /(\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~)/;

/** Variáveis únicas, na ordem em que aparecem no texto. */
export function extractVariables(...texts) {
  const seen = new Set();
  for (const text of texts) {
    if (!text) continue;
    for (const match of text.matchAll(new RegExp(VAR_SOURCE, "g"))) {
      seen.add(match[1]);
    }
  }
  return [...seen];
}

/**
 * Valores de exemplo para cada variável. Usa os exemplos que a Meta guarda no
 * template (`example.body_text`) quando existem; caso contrário, um texto
 * genérico legível.
 */
export function buildSampleValues(template, variables) {
  const body = template?.components?.find((c) => c.type === "BODY");
  const header = template?.components?.find((c) => c.type === "HEADER");
  const bodyExamples = body?.example?.body_text?.[0] || [];
  const headerExamples = header?.example?.header_text || [];
  const bodyVars = extractVariables(body?.text);

  const values = {};
  variables.forEach((name, index) => {
    const bodyIndex = bodyVars.indexOf(name);
    values[name] =
      (bodyIndex >= 0 ? bodyExamples[bodyIndex] : undefined) ||
      headerExamples[0] ||
      `Variável ${/^\d+$/.test(name) ? name : index + 1}`;
  });
  return values;
}

function renderVariables(text, ctx, keyPrefix) {
  const parts = text.split(new RegExp(VAR_SOURCE, "g"));
  // split com grupo de captura alterna: texto, nome da variável, texto, ...
  return parts.map((part, i) => {
    if (i % 2 === 0) return part;
    const value = ctx.values?.[part];
    if (ctx.mode === "example" && value) return value;
    return (
      <span
        key={`${keyPrefix}-v${i}`}
        className="rounded px-0.5 font-mono text-[0.9em]"
        style={{ background: "var(--wa-var-bg)", color: "var(--wa-var-fg)" }}
      >
        {`{{${part}}}`}
      </span>
    );
  });
}

function renderInline(text, ctx, keyPrefix) {
  return text.split(FORMAT_REGEX).map((part, i) => {
    const key = `${keyPrefix}-${i}`;
    if (i % 2 === 1) {
      const inner = renderInline(part.slice(1, -1), ctx, key);
      if (part[0] === "*") return <strong key={key}>{inner}</strong>;
      if (part[0] === "_") return <em key={key}>{inner}</em>;
      return <s key={key}>{inner}</s>;
    }
    return <React.Fragment key={key}>{renderVariables(part, ctx, key)}</React.Fragment>;
  });
}

/**
 * @param {string} text
 * @param {{ values?: Record<string,string>, mode?: 'example'|'model' }} ctx
 */
export function renderTemplateText(text, ctx = {}) {
  if (!text) return null;
  return renderInline(text, { mode: "example", ...ctx }, "t");
}
